import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState, } from "../../../utils/GridUtils";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import * as Const from "../constants/MandateConstants";
import ColouredChip from "../../../component/ColouredChip";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../pages/atoms/MandateAtoms";
import { useSddRepository } from "../../../api/repositories/SddRepository";
import { Button, IconButton, Tooltip } from "@mui/material";
import { AddRounded, DescriptionRounded } from "@mui/icons-material";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { useNavigate } from "react-router-dom";
import { useAgGridLocale } from "../../../../../config/globalConfig";


const MandateTable = () => {
    const UNIQUE_ID = "fnn-sdd-mandates";

    const { t } = useTranslation("finance", { keyPrefix: "sdd.mandates" });

    const navigate = useNavigate();

    const { getMandates } = useSddRepository();

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();

    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef[]>([
        {
            field: "debtorName",
            headerName: t("table.debtor-name"),
            minWidth: 150,
            unSortIcon: true,
            sortable: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true,
        },
        {
            field: "debtorIban",
            headerName: t("table.debtor-iban"),
            minWidth: 150,
            unSortIcon: true,
            sortable: true,
            lockVisible: true,
        },
        {
            field: "subscriberFirstName",
            headerName: t("table.subscriber-name"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) => `${params.value} ${params.data.subscriberLastName}`
        },
        {
            field: "subscriberTaxCode",
            headerName: t("table.subscriber-tax-code"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "subscriptionDate",
            headerName: t("table.subscription-date"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 100,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                let color = Const.STATUSES.find(s => s.value === status)?.color?.split(".")[0];
                if (!color || color === "neutral") color = "default";
                return {
                    component: () => (
                            <ColouredChip name={status} color={color}/>
                    ),
                };
            },
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                            <FinanceDynamicActions>
                                <Tooltip arrow placement={"top"} title={t("table.actions.edit")}>
                                    <IconButton color="accent" onClick={() => {
                                        navigate(`/finance/payments/sdd/mandates/${params.data.id}`)
                                    }}>
                                        <DescriptionRounded />
                                    </IconButton>
                                </Tooltip>
                            </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ]);

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t("no-rows.title"),
            content: t("no-rows.content")
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                    createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].colId;
                let order =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].sort.toUpperCase();
                getMandates(startRow, endRow - startRow, sort, order, filterDefs)
                        .then((response) => {
                            let result = response.result;

                            if (result["total-amount"] === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result["total-amount"],
                            });
                        })
                        .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (<></>),
        renderActions: () => (
                <Button fullWidth color={"accent"} variant="contained" component="label"
                        startIcon={<AddRounded/>}
                        onClick={() => {
                            navigate('/finance/payments/sdd/mandates/wizard')
                        }}>
                    {t('buttons.create')}
                </Button>
        ),
    };

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        rowModelType="serverSide"
                        paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                        serverSideInfiniteScroll
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        gridOptions={gridOptions}
                        rowSelection={"multiple"}
                        suppressRowClickSelection
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(
                                    createDatasource(filterDefs, gridRef.current!.api)
                            );

                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true,
                                });
                            }
                        }}
                        onColumnMoved={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            if (gridRef.current!.api) {
                                setLocalGridState(UNIQUE_ID, {
                                    rows: gridRef.current!.api?.paginationGetPageSize(),
                                    cols: getLocalGridState(UNIQUE_ID).cols,
                                });
                            }
                        }}
                ></AgGridCustomTable>
            </>
    );
};

export default MandateTable;
